import { useEffect, useRef, useState } from "react";

/**
 * True once the element has been scrolled into view. Stays true afterwards.
 *
 * IntersectionObserver rather than a motion library: the whole requirement is
 * "add a class when this becomes visible", and shipping 40kB of animation
 * runtime to a marketing page to do that is a Core Web Vitals cost with
 * nothing to show for it. The animation itself is CSS.
 *
 * Two deliberate behaviours:
 *
 *   - It never un-sets. Elements that fade back out as you scroll up are
 *     distracting and make a long page feel unstable.
 *   - It resolves immediately under `prefers-reduced-motion`, so content is
 *     never gated behind an animation someone has asked not to see. That is
 *     the difference between reduced motion and reduced information.
 */
export default function useInView({ threshold = 0.18, rootMargin = "0px 0px -10% 0px" } = {}) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const reduced =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        // Also the fallback for anything without IntersectionObserver: show it.
        if (reduced || typeof IntersectionObserver === "undefined") {
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setInView(true);
                    observer.disconnect();
                }
            },
            { threshold, rootMargin }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [threshold, rootMargin]);

    return [ref, inView];
}
